/**
 * Register container.
 * @module components/theme/Register/Register
 */

import React, { Component } from 'react';
import { Helmet } from '@plone/volto/helpers';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Form, Toast } from '@plone/volto/components';
import { createUser } from '@plone/volto/actions';

const messages = defineMessages({
  title: {
    id: 'Registrierungsformular',
    defaultMessage: 'Registrierungsformular',
  },
  default: {
    id: 'Default',
    defaultMessage: 'Default',
  },
  firstnameTitle: {
    id: 'Vorname',
    defaultMessage: 'Vorname',
  },
  firstnameDescription: {
    id: 'Trage deinen Vornamen ein, z.B. John.',
    defaultMessage: 'Trage deinen Vornamen ein, z.B. John.',
  },
  lastnameTitle: {
    id: 'Nachname',
    defaultMessage: 'Nachname',
  },
  lastnameDescription: {
    id: 'Trage deinen Nachnamen ein, z.B. Smith.',
    defaultMessage: 'Trage deinen Nachnamen ein, z.B. Smith.',
  },
  companyTitle: {
    id: 'Unternehmen',
    defaultMessage: 'Unternehmen',
  },
  companyDescription: {
    id: 'Trage den Namen deines Unternehmen ein.',
    defaultMessage: 'Trage den Namen deines Unternehmen ein.',
  },
  emailTitle: {
    id: 'E-mail',
    defaultMessage: 'Organizations-E-mail',
  },
  emailDescription: {
    id: 'Trage deine e-Mail ein. Diese ist dein Login-Name. Wir schützen deine Privatsphäre und werden deine Daten nicht an Dritte weitergeben oder sie anderweitig verwenden.',
    defaultMessage:
      'Trage deine e-Mail ein. Diese ist dein Login-Name. Wir schützen deine Privatsphäre und werden deine Daten nicht an Dritte weitergeben oder sie anderweitig verwenden.',
  },
  agbTitle: {
    id: 'agbTitle',
    defaultMessage: 'Ich akzeptiere die Nutzungsbedingungen',
  },
  agbDescription: {
    id: 'agbDescription',
    defaultMessage:
      'Bitte akzeptieren die Nutzungsbedingungen bevor du dich registrierst.',
  },
  privacyTitle: {
    id: 'privacyTitle',
    defaultMessage: 'Ich akzeptiere die Datenschutzeinstellungen',
  },
  privacyDescription: {
    id: 'privacyDescription',
    defaultMessage:
      'Bitte akzeptieren die Datenschutzerklärung bevor du dich registrierst.',
  },
  successRegisterCompletedTitle: {
    id: 'Account Registrierung erfolgreich',
    defaultMessage: 'Account Registrierung erfolgreich',
  },
  successRegisterCompletedBody: {
    id: 'Der Registrierungsprozess war erfolgreich. Bitte prüfe deinen e-Mail-Eingang für weitere Informationen und um deinen Account zu aktivieren.',
    defaultMessage:
      'Der Registrierungsprozess war erfolgreich. Bitte prüfe deinen e-Mail-Eingang für weitere Informationen und um deinen Account zu aktivieren.',
  },
  register: {
    id: 'Registrieren',
    defaultMessage: 'Registrieren',
  },
});

/**
 * Register class.
 * @class Register
 * @extends Component
 */
class Register extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    createUser: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    error: PropTypes.shape({
      message: PropTypes.string,
    }),
  };

  /**
   * Default properties.
   * @property {Object} defaultProps Default properties.
   * @static
   */
  static defaultProps = {
    error: null,
  };

  /**
   * Constructor
   * @method constructor
   * @param {Object} props Component properties
   * @constructs WysiwygEditor
   */
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.state = {
      error: null,
    };
  }

  /**
   * Component will receive props
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.loading && nextProps.loaded) {
      toast.success(
        <Toast
          success
          title={this.props.intl.formatMessage(
            messages.successRegisterCompletedTitle,
          )}
          content={this.props.intl.formatMessage(
            messages.successRegisterCompletedBody,
          )}
        />,
      );
      this.props.history.push('/login');
    }
  }

  /**
   * On submit handler
   * @method onSubmit
   * @param {object} data Form data.
   * @returns {undefined}
   */
  onSubmit(data) {
    const fullname = `${data.firstname} ${data.lastname}`.trim();
    this.props.createUser({
      fullname: fullname,
      email: data.email,
      password: data.password,
    });
    this.setState({
      error: null,
    });
  }

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    return (
      <div id="page-register">
        <Helmet title={this.props.intl.formatMessage(messages.register)} />
        <Form
          onSubmit={this.onSubmit}
          title={this.props.intl.formatMessage(messages.title)}
          error={this.state.error || this.props.error}
          loading={this.props.loading}
          submitLabel={this.props.intl.formatMessage(messages.register)}
          schema={{
            fieldsets: [
              {
                id: 'default',
                title: this.props.intl.formatMessage(messages.default),
                fields: [
                  'firstname',
                  'lastname',
                  'company',
                  'email',
                  'agb',
                  'privacy',
                ],
              },
            ],
            properties: {
              firstname: {
                type: 'string',
                title: this.props.intl.formatMessage(messages.firstnameTitle),
                description: this.props.intl.formatMessage(
                  messages.firstnameDescription,
                ),
              },
              lastname: {
                type: 'string',
                title: this.props.intl.formatMessage(messages.lastnameTitle),
                description: this.props.intl.formatMessage(
                  messages.lastnameDescription,
                ),
              },
              company: {
                type: 'string',
                title: this.props.intl.formatMessage(messages.companyTitle),
                description: this.props.intl.formatMessage(
                  messages.companyDescription,
                ),
              },
              email: {
                type: 'string',
                title: this.props.intl.formatMessage(messages.emailTitle),
                description: this.props.intl.formatMessage(
                  messages.emailDescription,
                ),
              },
              agb: {
                type: 'boolean',
                title: this.props.intl.formatMessage(messages.agbTitle),
                description: this.props.intl.formatMessage(
                  messages.agbDescription,
                ),
              },
              privacy: {
                type: 'boolean',
                title: this.props.intl.formatMessage(messages.privacyTitle),
                description: this.props.intl.formatMessage(
                  messages.privacyDescription,
                ),
              },
            },
            required: [
              'firstname',
              'lastname',
              'company',
              'email',
              'agb',
              'privacy',
            ],
          }}
        />
      </div>
    );
  }
}

export default compose(
  withRouter,
  injectIntl,
  connect(
    (state) => ({
      loading: state.users.create.loading,
      loaded: state.users.create.loaded,
      error: state.users.create.error,
    }),
    { createUser },
  ),
)(Register);
